import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FaUsers,
  FaEdit,
  FaTrash,
  FaPlus,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaUserCheck,
  FaUserTimes,
  FaShieldAlt,
  FaKey,
  FaSync,
  FaFileDownload,
  FaUserTie,
  FaUserSlash,
  FaRegCopy,
  FaFilter,
  FaTimes,
  FaCheckCircle,
  FaExclamationTriangle,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";
import { toast } from "react-toastify";
import UserForm from "../../Components/forms/UserForm";
import useAuth from "../../useAuth";
import { server } from "../../server";

export default function UserList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  // Backend Metrics State
  const [totalCount, setTotalCount] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [inactiveCount, setInactiveCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [securityPolicy, setSecurityPolicy] = useState(
    "JWT & Access Policy",
  );

  // Modals & Actions
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isPasswordResetOpen, setIsPasswordResetOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);

  // Block / Unblock Confirmation Modal States
  const [confirmStatusModalOpen, setConfirmStatusModalOpen] = useState(false);
  const [userToToggle, setUserToToggle] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const { token } = useAuth();

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${server}/auth/all-users`, {
        params: { page, pageSize, search, status: statusFilter.toLowerCase() },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = res.data.data || [];
      setUsers(data);
      setTotalCount(
        res.data.totalCount !== undefined ? res.data.totalCount : data.length,
      );
      setActiveCount(
        res.data.activeCount !== undefined
          ? res.data.activeCount
          : data.filter((u) => u.isActive !== false).length,
      );
      setInactiveCount(
        res.data.inactiveCount !== undefined
          ? res.data.inactiveCount
          : data.filter((u) => u.isActive === false).length,
      );
      setTotalPages(
        res.data.totalPages
          ? res.data.totalPages
          : Math.ceil((res.data.totalCount || data.length) / pageSize) || 1,
      );
      setSecurityPolicy(res.data.securityPolicy || "JWT & Access Policy");
    } catch (error) {
      toast.error("Failed to load user security accounts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    const timer = setTimeout(() => {
      fetchUsers();
    }, 300);
    return () => clearTimeout(timer);
  }, [search, page, statusFilter, token]);

  const handleDelete = async (id, name) => {
    if (
      window.confirm(
        `Are you sure you want to permanently delete user account '${name}'?`,
      )
    ) {
      try {
        await axios.delete(`${server}/auth/users/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        toast.success("User account deleted successfully");
        fetchUsers();
      } catch (error) {
        toast.error("Error deleting user account");
      }
    }
  };

  const openStatusConfirmModal = (user) => {
    setUserToToggle(user);
    setConfirmStatusModalOpen(true);
  };

  const handleConfirmToggleStatus = async () => {
    if (!userToToggle) return;
    const targetStatus = !userToToggle.isActive;
    const actionText = targetStatus ? "activated" : "deactivated & blocked";
    try {
      setIsUpdatingStatus(true);
      await axios.put(
        `${server}/auth/users/${userToToggle.id}/status`,
        { isActive: targetStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      toast.success(
        `Account for @${userToToggle.email || userToToggle.username || userToToggle.fullName} ${actionText}`,
      );
      setConfirmStatusModalOpen(false);
      setUserToToggle(null);
      fetchUsers();
    } catch (error) {
      toast.error("Error updating user status");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleAdminResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      toast.warning("Password must be at least 6 characters long.");
      return;
    }
    try {
      setIsSubmittingPassword(true);
      await axios.put(
        `${server}/auth/users/${resetUser.id}/reset-password`,
        { newPassword },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      toast.success(
        `Password reset successfully for ${resetUser.fullName || resetUser.email}!`,
      );
      setIsPasswordResetOpen(false);
      setNewPassword("");
      setResetUser(null);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to reset user password",
      );
    } finally {
      setIsSubmittingPassword(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.info(`Copied '${text}' to clipboard!`, { autoClose: 1500 });
  };

  const exportCSV = () => {
    if (!users.length) {
      toast.info("No data available to export");
      return;
    }
    const headers = [
      "ID",
      "Full Name",
      "Email",
      "Mobile",
      "Role",
      "Designation",
      "Status",
    ];
    const rows = users.map((u) => [
      u.id,
      `"${u.fullName || u.username || ""}"`,
      `"${u.email || ""}"`,
      `"${u.mobileNo || ""}"`,
      `"${typeof u.role === "object" ? u.role?.name : u.role || u.userType || "User"}"`,
      `"${u.designation || ""}"`,
      u.isActive !== false ? "Active" : "Disabled",
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `User_Security_Report_${new Date().toISOString().slice(0, 10)}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("User Security report exported to CSV!");
  };

  // Filter logic on current list
  const filteredUsers = users.filter((u) => {
    const roleName =
      typeof u.role === "object"
        ? u.role?.name?.toUpperCase()
        : (u.role || u.userType || "").toString().toUpperCase();
    const matchesRole =
      roleFilter === "ALL" || roleName.includes(roleFilter.toUpperCase());
    const matchesStatus =
      statusFilter === "ALL" ||
      (statusFilter === "ACTIVE" && u.isActive !== false) ||
      (statusFilter === "DISABLED" && u.isActive === false);
    return matchesRole && matchesStatus;
  });

  const getRoleBadgeStyle = (user) => {
    const r = (
      typeof user.role === "object"
        ? user.role?.name
        : user.role || user.userType || ""
    )
      .toString()
      .toLowerCase();
    if (r.includes("admin"))
      return "bg-indigo-100 dark:bg-indigo-950/80 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800";
    if (r.includes("teacher"))
      return "bg-emerald-100 dark:bg-emerald-950/80 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800";
    if (r.includes("staff"))
      return "bg-amber-100 dark:bg-amber-950/80 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800";
    return "bg-purple-100 dark:bg-purple-950/80 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800";
  };

  return (
    <div className="p-4 max-w-[1400px] mx-auto min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      {/* Premium Top Header Bar */}
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl px-5 py-3 mb-4 shadow-xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-indigo-500 to-indigo-700 text-white rounded-xl shadow-xs">
            <FaShieldAlt size={18} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-black text-gray-900 dark:text-white leading-tight">
                User Security & System Access
              </h1>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping"></span>{" "}
                Live Security
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold">
              Manage administrator accounts, security credentials, role
              assignments, and access policies
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
          <button
            onClick={fetchUsers}
            className="p-2 text-gray-600 dark:text-slate-300 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-xl transition font-bold text-xs"
            title="Refresh Account Data"
          >
            <FaSync
              className={loading ? "animate-spin text-indigo-500" : ""}
              size={12}
            />
          </button>

          <button
            onClick={exportCSV}
            className="bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-gray-800 dark:text-slate-200 px-3 py-1.5 rounded-xl font-bold text-xs transition border border-gray-200 dark:border-slate-700 flex items-center gap-1.5"
            title="Export User Security CSV"
          >
            <FaFileDownload size={11} className="text-indigo-500" /> Export CSV
          </button>

          <button
            onClick={() => {
              setSelectedUser(null);
              setIsModalOpen(true);
            }}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3.5 py-1.5 rounded-xl font-black text-xs transition shadow-xs flex items-center gap-1.5"
          >
            <FaPlus size={10} /> Add System User
          </button>
        </div>
      </div>

      {/* KPI Stat Cards (Clickable - Powered by Backend API Filtering) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        {/* Card 1: Total Registered Users */}
        <div
          onClick={() => {
            setStatusFilter("ALL");
            setPage(1);
          }}
          className={`p-3 rounded-xl border flex items-center justify-between cursor-pointer transition-all duration-200 select-none ${
            statusFilter === "ALL"
              ? "bg-indigo-50/60 dark:bg-indigo-950/40 border-indigo-500 ring-2 ring-indigo-500/30 scale-[1.02] shadow-xs"
              : "bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800 hover:border-indigo-300 hover:shadow-xs"
          }`}
          title="Click to view All User Accounts"
        >
          <div>
            <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">
              Total Registered Users
            </span>
            <div className="text-xl font-black text-indigo-600 dark:text-indigo-400 mt-0.5">
              {totalCount}
            </div>
            <span className="text-[9px] text-gray-400 font-semibold">
              System Accounts
            </span>
          </div>
          <div className="p-2.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-xl">
            <FaUsers size={16} />
          </div>
        </div>

        {/* Card 2: Active User Accounts */}
        <div
          onClick={() => {
            setStatusFilter("ACTIVE");
            setPage(1);
          }}
          className={`p-3 rounded-xl border flex items-center justify-between cursor-pointer transition-all duration-200 select-none ${
            statusFilter === "ACTIVE"
              ? "bg-emerald-50/60 dark:bg-emerald-950/40 border-emerald-500 ring-2 ring-emerald-500/30 scale-[1.02] shadow-xs"
              : "bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800 hover:border-emerald-300 hover:shadow-xs"
          }`}
          title="Click to view Active User Accounts"
        >
          <div>
            <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">
              Active User Accounts
            </span>
            <div className="text-xl font-black text-emerald-600 dark:text-emerald-400 mt-0.5">
              {activeCount}
            </div>
            <span className="text-[9px] text-emerald-600 dark:text-emerald-400 font-semibold font-mono">
              {totalCount > 0
                ? `${Math.round((activeCount / totalCount) * 100)}% Active`
                : "100% Active"}
            </span>
          </div>
          <div className="p-2.5 bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 rounded-xl">
            <FaUserTie size={16} />
          </div>
        </div>

        {/* Card 3: Deactivated Users */}
        <div
          onClick={() => {
            setStatusFilter("DISABLED");
            setPage(1);
          }}
          className={`p-3 rounded-xl border flex items-center justify-between cursor-pointer transition-all duration-200 select-none ${
            statusFilter === "DISABLED"
              ? "bg-rose-50/60 dark:bg-rose-950/40 border-rose-500 ring-2 ring-rose-500/30 scale-[1.02] shadow-xs"
              : "bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800 hover:border-rose-300 hover:shadow-xs"
          }`}
          title="Click to view Deactivated / Blocked Users"
        >
          <div>
            <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">
              Deactivated Users
            </span>
            <div className="text-xl font-black text-rose-600 dark:text-rose-400 mt-0.5">
              {inactiveCount}
            </div>
            <span className="text-[9px] text-rose-500 font-semibold">
              Blocked Users
            </span>
          </div>
          <div className="p-2.5 bg-rose-50 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 rounded-xl">
            <FaUserSlash size={16} />
          </div>
        </div>
      </div>

      {/* Search Toolbar & Filter Controls */}
      <div className="bg-white dark:bg-slate-900 p-3 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs mb-4 flex flex-col md:flex-row justify-between items-center gap-3">
        <div className="relative w-full md:w-80">
          <FaSearch className="absolute left-3 top-2.5 text-gray-400 dark:text-slate-500 text-xs" />
          <input
            type="text"
            className="w-full pl-8 pr-3 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-900 dark:text-white outline-none focus:border-indigo-500 transition"
            placeholder="Search by Name, Email, Username, Designation..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>

        {/* Filter Pills */}
        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto justify-end">
          {/* Status Filter */}
          <div className="flex items-center bg-gray-100 dark:bg-slate-800 p-1 rounded-xl text-xs font-bold">
            <button
              onClick={() => setStatusFilter("ALL")}
              className={`px-2.5 py-1 rounded-lg text-[10px] transition ${
                statusFilter === "ALL"
                  ? "bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-xs"
                  : "text-gray-500 dark:text-slate-400"
              }`}
            >
              All Status
            </button>
            <button
              onClick={() => setStatusFilter("ACTIVE")}
              className={`px-2.5 py-1 rounded-lg text-[10px] transition ${
                statusFilter === "ACTIVE"
                  ? "bg-white dark:bg-slate-900 text-emerald-600 dark:text-emerald-400 shadow-xs"
                  : "text-gray-500 dark:text-slate-400"
              }`}
            >
              Active
            </button>
            <button
              onClick={() => setStatusFilter("DISABLED")}
              className={`px-2.5 py-1 rounded-lg text-[10px] transition ${
                statusFilter === "DISABLED"
                  ? "bg-white dark:bg-slate-900 text-rose-600 dark:text-rose-400 shadow-xs"
                  : "text-gray-500 dark:text-slate-400"
              }`}
            >
              Blocked
            </button>
          </div>

          {/* Role Select Filter */}
          <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 px-2.5 py-1 rounded-xl text-xs font-bold">
            <FaFilter className="text-gray-400 text-[10px]" />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="bg-transparent text-xs font-bold text-gray-800 dark:text-white outline-none cursor-pointer"
            >
              <option value="ALL" className="dark:bg-slate-900">
                All Roles
              </option>
              <option value="ADMIN" className="dark:bg-slate-900">
                Admin
              </option>
              <option value="TEACHER" className="dark:bg-slate-900">
                Teacher
              </option>
              <option value="STAFF" className="dark:bg-slate-900">
                Staff
              </option>
              <option value="STUDENT" className="dark:bg-slate-900">
                Student
              </option>
            </select>
          </div>
        </div>
      </div>

      {/* High-Density Table Display */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs overflow-hidden mb-4">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-800/80 text-gray-500 dark:text-slate-400 font-black text-[10px] uppercase border-b border-gray-200 dark:border-slate-800">
                <th className="p-2.5">User Account Profile</th>
                <th className="p-2.5">Role & Designation</th>
                <th className="p-2.5">Contact Details</th>
                <th className="p-2.5">Access Status</th>
                <th className="p-2.5 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td
                    colSpan="5"
                    className="text-center py-12 text-indigo-600 font-bold"
                  >
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                    Loading system user security accounts...
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className="text-center py-12 text-gray-400 italic"
                  >
                    No matching user security accounts found.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => {
                  const roleName =
                    typeof user.role === "object"
                      ? user.role?.name
                      : user.role || user.userType || "User";
                  const nameDisplay =
                    user.fullName ||
                    (typeof user.name === "object"
                      ? user.name?.name
                      : user.name) ||
                    user.username ||
                    "System User";
                  const usernameHandle =
                    user.username ||
                    user.email?.split("@")[0] ||
                    `user_${user.id}`;

                  return (
                    <tr
                      key={user.id}
                      className="hover:bg-gray-50 dark:hover:bg-slate-800/40 transition"
                    >
                      <td className="p-2.5 whitespace-nowrap">
                        <div className="flex items-center gap-2.5">
                          <div className="relative">
                            <div className="h-8 w-8 rounded-full bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 font-black text-xs flex items-center justify-center border border-indigo-200 dark:border-indigo-800 shrink-0">
                              {nameDisplay[0]?.toUpperCase() || "U"}
                            </div>
                            <span
                              className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-slate-900 ${
                                user.isActive !== false
                                  ? "bg-emerald-500"
                                  : "bg-rose-500"
                              }`}
                            ></span>
                          </div>
                          <div>
                            <div className="font-black text-gray-900 dark:text-white text-xs flex items-center gap-1.5">
                              {nameDisplay}
                            </div>
                            <div className="text-[10px] text-gray-400 font-mono font-medium flex items-center gap-1">
                              <span>@{usernameHandle}</span>
                              <button
                                onClick={() => copyToClipboard(usernameHandle)}
                                className="hover:text-indigo-500 transition"
                                title="Copy Handle"
                              >
                                <FaRegCopy size={9} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="p-2.5 whitespace-nowrap">
                        <div className="flex flex-col gap-0.5 items-start">
                          <span
                            className={`px-2 py-0.5 rounded-md text-[10px] font-black border ${getRoleBadgeStyle(user)}`}
                          >
                            {roleName}
                          </span>
                          {user.designation && (
                            <span className="text-[9px] text-gray-500 dark:text-slate-400 font-semibold">
                              {user.designation}
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="p-2.5 whitespace-nowrap">
                        <div className="text-xs font-medium text-gray-700 dark:text-slate-300">
                          {user.email || "N/A"}
                        </div>
                        <div className="text-[10px] text-gray-400 font-mono font-semibold">
                          {user.mobileNo || "-"}
                        </div>
                      </td>

                      <td className="p-2.5 font-bold whitespace-nowrap">
                        <button
                          onClick={() => openStatusConfirmModal(user)}
                          className={`px-2.5 py-0.5 rounded-full text-[10px] font-black transition cursor-pointer flex items-center gap-1 border ${
                            user.isActive !== false
                              ? "bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800 hover:bg-emerald-200"
                              : "bg-rose-100 dark:bg-rose-950 text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-800 hover:bg-rose-200"
                          }`}
                          title="Click to toggle user status"
                        >
                          {user.isActive !== false ? (
                            <FaCheckCircle size={9} />
                          ) : (
                            <FaUserSlash size={9} />
                          )}
                          {user.isActive !== false ? "Active" : "Blocked"}
                        </button>
                      </td>

                      <td className="p-2.5 text-center whitespace-nowrap">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setIsModalOpen(true);
                            }}
                            className="p-1.5 text-indigo-600 bg-indigo-50 dark:bg-indigo-950 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900 transition"
                            title="Edit User Account"
                          >
                            <FaEdit size={11} />
                          </button>

                          <button
                            onClick={() => {
                              setResetUser(user);
                              setNewPassword("");
                              setIsPasswordResetOpen(true);
                            }}
                            className="p-1.5 text-purple-600 bg-purple-50 dark:bg-purple-950 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900 transition"
                            title="Reset User Password"
                          >
                            <FaKey size={11} />
                          </button>

                          <button
                            onClick={() => openStatusConfirmModal(user)}
                            className={`p-1.5 rounded-lg transition ${
                              user.isActive !== false
                                ? "text-amber-600 bg-amber-50 dark:bg-amber-950 hover:bg-amber-100 dark:hover:bg-amber-900"
                                : "text-emerald-600 bg-emerald-50 dark:bg-emerald-950 hover:bg-emerald-100 dark:hover:bg-emerald-900"
                            }`}
                            title={
                              user.isActive !== false
                                ? "Disable & Block User"
                                : "Enable User Account"
                            }
                          >
                            {user.isActive !== false ? (
                              <FaUserTimes size={11} />
                            ) : (
                              <FaUserCheck size={11} />
                            )}
                          </button>

                          <button
                            onClick={() => handleDelete(user.id, nameDisplay)}
                            className="p-1.5 text-rose-600 bg-rose-50 dark:bg-rose-950 rounded-lg hover:bg-rose-100 dark:hover:bg-rose-900 transition"
                            title="Delete User Account"
                          >
                            <FaTrash size={11} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Bar */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center bg-white dark:bg-slate-900 p-3 rounded-2xl border border-gray-200 dark:border-slate-800 text-xs font-bold text-gray-700 dark:text-slate-300">
          <span>
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className="px-3 py-1.5 bg-gray-100 dark:bg-slate-800 rounded-lg disabled:opacity-40 hover:bg-gray-200 dark:hover:bg-slate-700 transition"
            >
              <FaChevronLeft size={10} />
            </button>
            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              className="px-3 py-1.5 bg-gray-100 dark:bg-slate-800 rounded-lg disabled:opacity-40 hover:bg-gray-200 dark:hover:bg-slate-700 transition"
            >
              <FaChevronRight size={10} />
            </button>
          </div>
        </div>
      )}

      {/* User Registration / Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-xs">
          <UserForm
            user={selectedUser}
            userToEdit={selectedUser}
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSuccess={fetchUsers}
          />
        </div>
      )}

      {/* Admin Reset Password Modal */}
      {isPasswordResetOpen && resetUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-xs">
          <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-800 overflow-hidden flex flex-col font-sans transition-colors duration-300">
            <div className="flex justify-between items-center px-5 py-3 border-b border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800/80">
              <h3 className="text-sm font-black text-gray-900 dark:text-white flex items-center gap-2">
                <FaKey className="text-purple-600 dark:text-purple-400" /> Reset
                Password for {resetUser.fullName || resetUser.email}
              </h3>
              <button
                onClick={() => setIsPasswordResetOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 text-xs font-bold transition"
              >
                <FaTimes />
              </button>
            </div>

            <form
              onSubmit={handleAdminResetPassword}
              className="p-5 space-y-4 text-xs"
            >
              <div className="p-3 bg-purple-50 dark:bg-purple-950/40 rounded-xl border border-purple-100 dark:border-purple-900/50 text-[11px] text-purple-800 dark:text-purple-300 font-semibold">
                You are setting a new credential password for account{" "}
                <strong>@{resetUser.email || resetUser.username}</strong>.
              </div>

              <div>
                <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1">
                  New Password *
                </label>
                <div className="relative">
                  <input
                    type={showResetPassword ? "text" : "password"}
                    required
                    minLength={6}
                    placeholder="Enter new password (min 6 chars)"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full p-2.5 pr-10 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-900 dark:text-white outline-none focus:border-purple-500 transition"
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 transition focus:outline-none"
                    title={showResetPassword ? "Hide password" : "Show password"}
                  >
                    {showResetPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-gray-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsPasswordResetOpen(false)}
                  className="px-3.5 py-1.5 rounded-xl font-bold text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingPassword}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-1.5 rounded-xl font-black transition shadow-xs flex items-center gap-1.5 disabled:opacity-50"
                >
                  {isSubmittingPassword ? "Resetting..." : "Reset Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Custom Confirmation Modal for Block / Unblock User */}
      {confirmStatusModalOpen && userToToggle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-xs">
          <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-800 overflow-hidden flex flex-col font-sans transition-colors duration-300">
            {/* Header */}
            <div className="flex justify-between items-center px-5 py-3 border-b border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800/80">
              <h3 className="text-sm font-black text-gray-900 dark:text-white flex items-center gap-2">
                {userToToggle.isActive !== false ? (
                  <FaUserSlash className="text-rose-600 dark:text-rose-400" />
                ) : (
                  <FaUserCheck className="text-emerald-600 dark:text-emerald-400" />
                )}
                {userToToggle.isActive !== false ? "Block Account Confirmation" : "Activate Account Confirmation"}
              </h3>
              <button
                onClick={() => {
                  setConfirmStatusModalOpen(false);
                  setUserToToggle(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 text-xs font-bold transition"
              >
                <FaTimes />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-4 text-xs">
              <div className="flex items-start gap-3 p-3 rounded-xl border bg-gray-50 dark:bg-slate-800/60 border-gray-200 dark:border-slate-700">
                <div className="p-2.5 rounded-full bg-rose-100 dark:bg-rose-950/70 text-rose-600 dark:text-rose-400 shrink-0">
                  <FaExclamationTriangle size={18} />
                </div>
                <div>
                  <h4 className="font-black text-gray-900 dark:text-white text-xs">
                    {userToToggle.isActive !== false ? "Confirm Account Block" : "Confirm Account Activation"}
                  </h4>
                  <p className="text-[11px] text-gray-500 dark:text-slate-400 font-medium mt-0.5">
                    {userToToggle.isActive !== false
                      ? "Are you sure you want to block this user account? The user will be immediately blocked from logging in or making API requests."
                      : "Are you sure you want to reactivate this user account? Normal system access will be restored."}
                  </p>
                </div>
              </div>

              {/* User Account Info Card */}
              <div className="p-3 bg-indigo-50/50 dark:bg-indigo-950/30 rounded-xl border border-indigo-100 dark:border-indigo-900/50 space-y-1">
                <div className="flex justify-between items-center text-[11px]">
                  <span className="text-gray-400 font-bold uppercase text-[9px]">Target User</span>
                  <span className="font-black text-gray-900 dark:text-white">
                    {userToToggle.fullName || userToToggle.username || "System User"}
                  </span>
                </div>
                <div className="flex justify-between items-center text-[11px]">
                  <span className="text-gray-400 font-bold uppercase text-[9px]">Email / Handle</span>
                  <span className="font-mono font-bold text-indigo-600 dark:text-indigo-400">
                    @{userToToggle.username || userToToggle.email?.split("@")[0] || userToToggle.id}
                  </span>
                </div>
                <div className="flex justify-between items-center text-[11px]">
                  <span className="text-gray-400 font-bold uppercase text-[9px]">Current Status</span>
                  <span className={`font-extrabold ${userToToggle.isActive !== false ? "text-emerald-600" : "text-rose-600"}`}>
                    {userToToggle.isActive !== false ? "Active" : "Blocked"}
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-2 pt-3 border-t border-gray-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => {
                    setConfirmStatusModalOpen(false);
                    setUserToToggle(null);
                  }}
                  className="px-3.5 py-1.5 rounded-xl font-bold text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isUpdatingStatus}
                  onClick={handleConfirmToggleStatus}
                  className={`px-4 py-1.5 rounded-xl font-black text-white transition shadow-xs flex items-center gap-1.5 disabled:opacity-50 ${
                    userToToggle.isActive !== false
                      ? "bg-rose-600 hover:bg-rose-700"
                      : "bg-emerald-600 hover:bg-emerald-700"
                  }`}
                >
                  {isUpdatingStatus ? (
                    "Processing..."
                  ) : userToToggle.isActive !== false ? (
                    <>
                      <FaUserSlash size={11} /> Confirm Block
                    </>
                  ) : (
                    <>
                      <FaUserCheck size={11} /> Confirm Activate
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
